// src/services/CaptureService.ts
import { BaseApiService } from "./ApiService";
import { apiConfig } from "../config/apiConfig";
import { appConfig } from "../config/app-config";
import { formatBytes } from "../utils/format-bytes";
import { getTurnstileToken } from "./TurnstileService";
import type { HttpRequestConfig, HttpResponse } from "./ApiService";

/**
 * The batch ceiling this service enforces.
 *
 * A CLIENT policy — nothing in `functions/` rejects a longer list — and it was written out as a
 * literal 20 in four places: the two guards below, BatchUrlInput's error text, and
 * StorybookImport's MAX_SELECTED. Raising it meant finding all four, and the ones you missed
 * disagreed silently: a list the input accepted, refused by the service that sent it.
 */
const MAX_BATCH_URLS = appConfig.limits.batchUrls;

export interface CaptureWebsiteRequest {
  url?: string;
  urls?: string[];
  batch?: boolean;
  viewports: string[];
  themes: string[];
  userAgent?: string;
  /** Capture only the element matching this CSS selector (whole page if unset). */
  selector?: string;
}

export interface CaptureFromCodeRequest {
  html: string;
  css: string;
  viewports: string[];
  themes: string[];
}

export interface CaptureFromZipRequest {
  viewports: string[];
  themes: string[];
  customViewports?: Record<string, string>;
}


export interface CaptureResponse {
  url?: string;
  /**
   * Set on a 202 when the capture was SCHEDULED rather than run: the free tier meters throughput,
   * not count, so a capture is never refused — only started later. `startsAt` is when, and the
   * client is expected to show it. See functions/_api/workers/lib/rate-limiter-do.ts.
   */
  scheduled?: boolean;
  startsAt?: string;
  deferredMs?: number;
  /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
  isSuccess: boolean;
  executionTime: string;
  requestId: string;
  message?: string;
  results?: Array<{
    url: string;
    success: boolean;
    data?: {
      fileKey: string;
      publicUrl: string;
      captureCount: number;
    };
    processingTime: number;
  }>;
  summary?: {
    totalUrls: number;
    successCount: number;
    failureCount: number;
    totalProcessingTime: number;
  };
}

export interface ZipCaptureResponse extends CaptureResponse {
  hostingSiteInfo?: {
    siteId: string;
  };
  captureInfo?: {
    viewports: string[];
    themes: string[];
    captureCount: number;
  };
}

export interface CaptureParams {
  figmaUID: string;
  figmaUserName: string;
  /**
   * The tier the CLIENT believes it has, used for one client-side pre-check: refusing a batch run
   * with a clear message instead of letting the second URL hit the cooldown and fail obscurely.
   *
   * It is not sent to the server and must never be. auth.ts resolves membership from the database
   * on every request — "IGNORE client-provided membership - always fetch from database" — so this
   * is a UX affordance, not an entitlement. The server enforcement for batch is the per-request
   * cooldown, which stops a FREE account after the first URL regardless of what this says.
   */
  membership: string;
}

export interface CaptureResult {
  url?: string;
  urls?: string[];
  jsonData: any;
  /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
  hostingSiteInfo?: { siteId: string };
  batchResults?: Array<{
    url: string;
    success: boolean;
    jsonData?: any;
    error?: string;
  }>;
}

export interface ProgressUpdate {
  status: string;
  progress: number;
  stage: 'init' | 'upload' | 'processing' | 'fetch' | 'complete' | 'error';
  detail?: string;
  timeElapsed?: number;
  estimatedTimeRemaining?: number;
}

export interface CancellationToken {
  isCancelled: boolean;
  cancel: () => void;
  onCancel: (callback: () => void) => void;
}

class CaptureService extends BaseApiService {
  private activeCancellationTokens = new Set<CancellationToken>();

  /**
   * Attach a fresh Turnstile token to every capture request.
   *
   * Overridden HERE rather than added at the four call sites, and rather than put in
   * `BaseApiService`, for one reason each:
   *
   *   - at the call sites it would be four copies and a fifth that forgets. Two of them are in
   *     the batch loop and the deferral retry, which re-issue a request built once — a token
   *     pinned into that construction would be REUSED on the retry and refused as a duplicate.
   *     Minting it inside the transport means every re-issue mints its own.
   *   - in the base class it would challenge the status polls and the health probe too. Those are
   *     cheap reads the server does not gate, and a token on each would spend a round trip
   *     against Cloudflare for nothing.
   *
   * A null token is still sent. The server is the only side that knows whether a secret is bound,
   * and refusing to send here would break every preview deployment — see TurnstileService.
   */
  protected override async makeRequest<T>(
    config: HttpRequestConfig,
    context: string,
    cancelKey?: string,
  ): Promise<HttpResponse<T>> {
    const token = await getTurnstileToken({ siteKey: appConfig.turnstileSiteKey });
    const withToken: HttpRequestConfig = token
      ? { ...config, headers: { ...(config.headers || {}), "CF-Turnstile-Response": token } }
      : config;
    return super.makeRequest<T>(withToken, context, cancelKey);
  }

  createCancellationToken(): CancellationToken {
    const callbacks: Array<() => void> = [];
    const token: CancellationToken = {
      isCancelled: false,
      cancel: () => {
        token.isCancelled = true;
        callbacks.forEach(callback => {
          try {
            callback();
          } catch {
            // Silent cancellation error - no console output
          }
        });
        this.activeCancellationTokens.delete(token);
      },
      onCancel: (callback: () => void) => {
        callbacks.push(callback);
      }
    };
    
    this.activeCancellationTokens.add(token);
    return token;
  }

  private createProgressReporter(
    startTime: number,
    progressCallback?: (update: ProgressUpdate) => void
  ) {
    if (!progressCallback) {
      return () => {};
    }

    return (status: string, progress: number, stage: ProgressUpdate['stage'], detail?: string) => {
      const timeElapsed = Date.now() - startTime;
      const estimatedTimeRemaining = progress > 0 ? (timeElapsed * (100 - progress)) / progress : undefined;
      
      progressCallback({
        status,
        progress,
        stage,
        detail,
        timeElapsed,
        estimatedTimeRemaining
      });
    };
  }

  /**
   * Beyond this the client hands the decision back rather than holding the UI. Ten minutes is
   * long enough to cover an ordinary burst overrun and short enough that nobody sits staring at
   * a spinner wondering whether it is broken.
   */
  private static readonly MAX_CLIENT_WAIT_MS = 10 * 60 * 1000;

  /**
   * Wait out a scheduled start, reporting the remaining time every second.
   *
   * The reporting is the point, not the waiting: a deferral the interface hides is worse for the
   * user than the refusal it replaced, so the countdown is pushed through `progressCallback`
   * where the UI already shows capture status.
   */
  private async countdown(
    waitMs: number,
    progressCallback?: (status: string, progress: number) => void
  ): Promise<void> {
    /**
     * CANCELLABLE, which a bare timer is not.
     *
     * `cancelAllRequests` aborts in-flight REQUESTS, and a countdown is not one — so without this
     * the Cancel button did nothing for the whole wait and only took effect when the retry fired
     * minutes later. Registering a controller under the capture's own key puts the wait on the
     * same abort path the button already drives.
     */
    const controller = this.createCancelToken('capture_website');
    const endsAt = Date.now() + waitMs;
    try {
      for (;;) {
        if (controller.signal.aborted) throw new Error('Capture cancelled');
        const left = endsAt - Date.now();
        if (left <= 0) return;
        if (progressCallback) {
          const mins = Math.floor(left / 60000);
          const secs = Math.ceil((left % 60000) / 1000);
          const human = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
          progressCallback(`Starting in ${human} — Pro starts now`, 5);
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, Math.min(1000, left));
          // Resolve early on abort so Cancel is felt within the tick, not at the end of it.
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timer);
            resolve();
          }, { once: true });
        });
      }
    } finally {
      // The retry issues its own token under this key; leaving ours registered would make the
      // next `cancelRequest` abort a controller nothing is waiting on.
      this.cancelTokenSources.delete('capture_website');
    }
  }

  /**
   * Wait out a scheduled start, if the server deferred this capture. Returns whether the caller
   * should re-issue the request.
   *
   * ONE implementation for both capture call sites, which is the point: the inline version of
   * this existed only in `captureWebsite`, so the batch loop fell through to
   * `if (!isSuccess) throw` and reported a deferral as "Server reported an error during capture".
   * Two copies of a response check is how that happens, so there is one.
   *
   * Throws rather than waits past the ceiling: holding the UI for longer than that is worse than
   * handing the decision back.
   */
  private async awaitScheduledStart(
    responseData: CaptureResponse,
    progressCallback?: (status: string, progress: number) => void,
    opts?: { noRetry?: boolean }
  ): Promise<boolean> {
    if (!responseData.scheduled || !responseData.startsAt) return false;

    const queuedMessage =
      responseData.message || "This capture is queued behind your recent ones.";
    if (opts?.noRetry) {
      // Deferred twice: the budget genuinely has no credit, and looping would hide an unbounded
      // wait behind a spinner.
      throw new Error(queuedMessage);
    }
    const waitMs = Math.max(0, new Date(responseData.startsAt).getTime() - Date.now());
    if (waitMs > CaptureService.MAX_CLIENT_WAIT_MS) throw new Error(queuedMessage);
    await this.countdown(waitMs, progressCallback);
    return true;
  }

  async captureWebsite(
    request: CaptureWebsiteRequest,
    params: CaptureParams,
    progressCallback?: (status: string, progress: number) => void,
    opts?: { noRetry?: boolean }
  ): Promise<CaptureResult> {
    this.validateCaptureRequest(request, params);

    if (progressCallback) {
      progressCallback("Initializing capture...", 0);
    }

    // No membership on the wire. auth.ts resolves it from the database on every request under an
    // explicit "IGNORE client-provided membership - always fetch from database", so sending it
    // achieved nothing — and a client-supplied entitlement sitting in the query string is the same
    // shape as the checkout price the client used to send, which was a real paywall bypass. It is
    // removed rather than left for someone to helpfully wire up.
    const queryParams = new URLSearchParams({
      figmaUID: params.figmaUID,
      figmaUserName: params.figmaUserName || "",
    });

    // Handle batch requests (PRO only)
    if (request.batch && request.urls && request.urls.length > 1) {
      return this.captureBatchUrls(request, params, progressCallback);
    }

    try {
      // Step 1: Send capture request
      if (progressCallback) {
        progressCallback("Sending capture request...", 10);
      }

      const response = await this.makeRequest<CaptureResponse>({
        method: 'POST',
        url: `${this.buildUrl(apiConfig.endpoints.captureToFile)}?${queryParams.toString()}`,
        data: request,
        headers: { "Content-Type": "application/json" },
        timeout: apiConfig.timeout.default
      }, "Website capture", "capture_website");

      const responseData = response.data;

      /**
       * SCHEDULED, not refused. The server answers 202 with `startsAt` when a free capture is
       * throttled on throughput rather than blocked — see functions/.../rate-limiter-do.ts.
       *
       * Waited out HERE, with a live countdown, and then re-issued. That is the whole point of
       * the design: the user is told exactly when their capture starts and it then starts by
       * itself. A silent wait would be worse than the refusal it replaced, which is why the
       * countdown is surfaced through `progressCallback` rather than swallowed.
       *
       * Checked BEFORE `isSuccess`, because a 202 deliberately carries `isSuccess: false` — it is
       * not a success yet. Reading it in the other order would turn every deferral into "Server
       * reported an error".
       */
      if (await this.awaitScheduledStart(responseData, progressCallback, opts)) {
        return this.captureWebsite(request, params, progressCallback, { noRetry: true });
      }

      if (!responseData.isSuccess) {
        throw CaptureService.bodyError(responseData, "Server reported an error during capture");
      }

      if (!responseData.url) {
        throw new Error("Missing URL in response data");
      }

      // Step 2: Fetch the JSON data
      if (progressCallback) {
        progressCallback("Website captured. Fetching design data...", 40);
      }

      const jsonData = await this.fetchDesignData(
        responseData.url,
        progressCallback ? (status, progress) => progressCallback(status, progress) : () => {}
      );

      if (progressCallback) {
        progressCallback("Design data ready", 100);
      }

      return {
        url: responseData.url,
        jsonData,
      };
    } catch (error: any) {
      this.failed(error, "capture website");
    }
  }

  private async captureBatchUrls(
    request: CaptureWebsiteRequest,
    params: CaptureParams,
    progressCallback?: (status: string, progress: number) => void
  ): Promise<CaptureResult> {
    // A client-side courtesy, not a gate: see CaptureParams.membership. Skipping it would not
    // grant anything — the cooldown blocks the second URL — it would just fail less clearly.
    if (params.membership !== "PRO") {
      throw new Error("PRO membership required for batch URL processing. Upgrade to process multiple URLs at once.");
    }

    if (!request.urls || request.urls.length === 0) {
      throw new Error("URLs array is required for batch processing");
    }

    if (request.urls.length > MAX_BATCH_URLS) {
      throw new Error(`Maximum ${MAX_BATCH_URLS} URLs allowed per batch request`);
    }

    const queryParams = new URLSearchParams({
      figmaUID: params.figmaUID,
      figmaUserName: params.figmaUserName || "",
    });

    // ONE REQUEST PER URL, in sequence.
    //
    // This used to POST the whole `{ urls, batch: true }` body to /capture-to-file and expect a
    // `results` array back. That endpoint reads `url` and nothing else — `grep -c "\burls\b"`
    // on the route is 0 — so every multi-story import answered "Valid URL is required" and threw.
    // There is no server-side batch to call.
    //
    // Sequential rather than concurrent, and deliberately so: each URL is a full headless page
    // load against the same per-user rate limit, and firing 20 at once is how you get throttled
    // instead of finished. It also gives honest per-URL progress.
    //
    // One URL failing does not fail the import: it lands in `batchResults` with its reason, and
    // the caller renders what did come back. A partial import beats losing 19 good captures.
    const batchResults: Array<{
      url: string;
      success: boolean;
      jsonData?: any;
      error?: string;
    }> = [];

    try {
      const total = request.urls.length;
      for (let i = 0; i < total; i++) {
        const target = request.urls[i];
        const base = (i / total) * 100;
        const span = 100 / total;
        try {
          if (progressCallback) {
            progressCallback(`Capturing ${i + 1} of ${total}: ${target}`, base + span * 0.1);
          }
          // One construction of this request, so the deferral retry below re-issues exactly what
          // was sent rather than a copy that can drift from it.
          const issue = () =>
            this.makeRequest<CaptureResponse>({
              method: 'POST',
              url: `${this.buildUrl(apiConfig.endpoints.captureToFile)}?${queryParams.toString()}`,
              // The batch fields are dropped: what goes out is a single-URL capture, which is the
              // only thing the endpoint understands. Everything else (viewports, themes, selector)
              // is carried through unchanged.
              data: { ...request, url: target, urls: undefined, batch: undefined },
              headers: { "Content-Type": "application/json" },
              timeout: apiConfig.timeout.default
            }, "Website capture", "capture_website");
          const response = await issue();

          const responseData = response.data;
          /**
           * A deferral is checked here too, BEFORE `isSuccess`, because a 202 deliberately
           * carries `isSuccess: false`. Without this the batch loop reported a scheduled capture
           * as "Server reported an error during capture" — which it did, until this was added.
           *
           * One retry per URL. The loop continues to the next URL either way, so a URL deferred
           * twice fails on its own rather than stopping the batch.
           */
          if (await this.awaitScheduledStart(responseData, progressCallback)) {
            // Re-issued through the SAME closure, not a hand-written copy of the request. A second
            // `makeRequest` here would be a third cancel-keyed capture call (which
            // capture-cancel.test.ts counts, and rightly) and a second place for the request shape
            // to drift — the duplication that caused the bug this block exists to fix.
            Object.assign(responseData, (await issue()).data);
            await this.awaitScheduledStart(responseData, progressCallback, { noRetry: true });
          }
          if (!responseData.isSuccess) {
            throw CaptureService.bodyError(responseData, "Server reported an error during capture");
          }
          if (!responseData.url) {
            throw new Error("Missing URL in response data");
          }
          const jsonData = await this.fetchDesignData(
            responseData.url,
                (status, progress) => {
              if (progressCallback) {
                progressCallback(`${target}: ${status}`, base + span * (0.5 + (progress / 100) * 0.5));
              }
            }
          );
          batchResults.push({ url: target, success: true, jsonData });
        } catch (error: any) {
          batchResults.push({
            url: target,
            success: false,
            error: error?.message || "Capture failed",
          });
        }
      }

      const successCount = batchResults.filter((r) => r.success).length;
      if (!successCount) {
        // Every one failed — report the first real reason rather than an empty success.
        throw new Error(batchResults[0]?.error || "No results returned from batch capture");
      }
      if (progressCallback) {
        progressCallback(`Batch processing complete: ${successCount}/${batchResults.length} successful`, 100);
      }

      return {
        urls: request.urls,
        // Each entry is ONE page's capture (its own array of viewport variants). Flattening them
        // into a single array would hand the renderer what looks like one page captured at many
        // viewports, and every frame would render the first page's data — so the per-page split
        // in `batchResults` is what callers must use.
        jsonData: batchResults.filter((r) => r.success).map((r) => r.jsonData),
        batchResults
      };
    } catch (error: any) {
      this.failed(error, "capture batch URLs");
    }
  }

  async captureFromCode(
    request: CaptureFromCodeRequest,
    params: CaptureParams,
    progressCallback?: (status: string, progress: number) => void
  ): Promise<CaptureResult> {
    this.validateCodeRequest(request, params);

    if (progressCallback) {
      progressCallback("Initializing HTML/CSS conversion...", 0);
    }

    const queryParams = new URLSearchParams({
      figmaUID: params.figmaUID,
      figmaUserName: params.figmaUserName || "",
    });

    try {
      // Step 1: Send capture request
      if (progressCallback) {
        progressCallback("Processing HTML/CSS...", 10);
      }

      const response = await this.makeRequest<CaptureResponse>({
        method: 'POST',
        url: `${this.buildUrl(apiConfig.endpoints.captureFromCode)}?${queryParams.toString()}`,
        data: request,
        headers: { "Content-Type": "application/json" },
        timeout: apiConfig.timeout.default
      }, "Code capture", "capture_code");

      const responseData = response.data;

      if (!responseData.isSuccess) {
        throw CaptureService.bodyError(responseData, "Server reported an error during HTML/CSS processing");
      }

      if (!responseData.url) {
        throw new Error("Missing URL in response data");
      }

      // Step 2: Fetch the JSON data
      if (progressCallback) {
        progressCallback("HTML/CSS processed. Fetching design data...", 40);
      }

      const jsonData = await this.fetchDesignData(
        responseData.url,
        progressCallback ? (status, progress) => progressCallback(status, progress) : () => {}
      );

      if (progressCallback) {
        progressCallback("Design data ready", 100);
      }

      return {
        url: responseData.url,
        jsonData,
      };
    } catch (error: any) {
      this.failed(error, "process HTML/CSS code");
    }
  }

  async captureFromZip(
    zipFile: File,
    request: CaptureFromZipRequest,
    params: CaptureParams,
    progressCallback?: (update: ProgressUpdate) => void,
    cancellationToken?: CancellationToken
  ): Promise<CaptureResult> {
    this.validateZipRequest(zipFile, request, params);
    
    const startTime = Date.now();
    const reportProgress = this.createProgressReporter(startTime, progressCallback);

    // Check cancellation
    if (cancellationToken?.isCancelled) {
      throw new Error('Operation was cancelled');
    }

    reportProgress("Initializing file upload and processing...", 0, 'init');

    const queryParams = new URLSearchParams({
      figmaUID: params.figmaUID,
      figmaUserName: params.figmaUserName || "",
    });

    try {
      // Step 1: Prepare FormData
      const fileType = zipFile.name.toLowerCase().endsWith('.zip') ? "ZIP" : "HTML";
      reportProgress(`Preparing ${fileType} file for upload...`, 10, 'init', `File size: ${formatBytes(zipFile.size)}`);

      const formData = this.prepareZipFormData(zipFile, request);

      // Step 2: Upload file
      if (cancellationToken?.isCancelled) {
        throw new Error('Operation was cancelled');
      }
      
      reportProgress("Uploading file...", 20, 'upload');

      const response = await this.makeRequest<ZipCaptureResponse>({
        method: 'POST',
        url: `${this.buildUrl(apiConfig.endpoints.captureFromZip)}?${queryParams.toString()}`,
        data: formData,
        timeout: apiConfig.timeout.upload,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercent = Math.round((progressEvent.loaded * 30) / progressEvent.total);
            reportProgress(
              `Uploading file... ${uploadPercent}%`,
              20 + uploadPercent,
              'upload',
              `${formatBytes(progressEvent.loaded)} of ${formatBytes(progressEvent.total)}`,
            );
          }
        }
      }, "ZIP capture", "capture_zip");

      const responseData = response.data;

      if (!responseData.isSuccess) {
        throw CaptureService.bodyError(responseData, "Server reported an error during file processing");
      }

      if (!responseData.url) {
        throw new Error("Missing URL in response data");
      }

      // Step 3: Fetch the JSON data
      if (cancellationToken?.isCancelled) {
        throw new Error('Operation was cancelled');
      }
      
      reportProgress("File processed successfully. Fetching design data...", 50, 'processing');

      const jsonData = await this.fetchDesignData(
        responseData.url,
        reportProgress,
        60, // Start progress at 60% for fetch phase
        cancellationToken
      );

      reportProgress("Design data ready", 100, 'complete');

      return {
        url: responseData.url,
        jsonData,
        hostingSiteInfo: responseData.hostingSiteInfo,
      };
    } catch (error: any) {
      reportProgress("Error occurred", 0, 'error', error.message);
      
      if (error.message === 'Operation was cancelled') {
        throw error;
      }
      
      this.failed(error, "process file");
    }
  }

  private async fetchDesignData(
    designUrl: string,
    reportProgress: (status: string, progress: number, stage: ProgressUpdate['stage'], detail?: string) => void,
    startProgress: number = 50,
    cancellationToken?: CancellationToken
  ): Promise<any> {
    const fetchOperation = async () => {
      if (cancellationToken?.isCancelled) {
        throw new Error('Operation was cancelled');
      }
      
      reportProgress("Downloading design data...", startProgress, 'fetch');

      const response = await this.fetchWithProxy(
        designUrl,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        },
      );

      const jsonText = await response.text();

      reportProgress("Processing design data...", startProgress + 30, 'fetch', 'Parsing JSON data');

      if (!jsonText || jsonText.trim().length === 0) {
        throw new Error("Received empty design data from the server");
      }

      try {
        const jsonData = JSON.parse(jsonText);

        if (!Array.isArray(jsonData)) {
          throw new Error("Invalid design data format: expected an array");
        }

        if (jsonData.length === 0) {
          throw new Error("Empty design data: no viewports captured");
        }

        return jsonData;
      } catch (parseError: any) {
        throw new Error(`Failed to parse design data: ${parseError?.message || 'Unknown parsing error'}`);
      }
    };

    return this.executeWithRetry(fetchOperation, "Fetch design data");
  }

  /**
   * Fetch, with the request registered so `cancelRequests` can abort it.
   *
   * The `proxyEnabled` branch that used to live here ended in
   * `throw new Error("CORS proxy functionality not implemented")` — a developer string on a user
   * path. It was unreachable, because nothing in the UI dispatched `toggleProxy`, so the flag was
   * permanently false. See the note on the same method in api/index.ts for why the whole
   * mechanism is removed rather than finished.
   */
  private async fetchWithProxy(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    this.pendingAbortControllers.push(controller);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (response.ok) return response;
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    } finally {
      this.pendingAbortControllers = this.pendingAbortControllers.filter((c) => c !== controller);
    }
  }

  private validateCaptureRequest(request: CaptureWebsiteRequest, params: CaptureParams): void {
    // For batch requests
    if (request.batch) {
      if (!request.urls || request.urls.length === 0) {
        throw new Error("URLs array is required for batch processing");
      }
      if (request.urls.length > MAX_BATCH_URLS) {
        throw new Error(`Maximum ${MAX_BATCH_URLS} URLs allowed per batch request`);
      }
      // Validate each URL
      request.urls.forEach((url, index) => {
        if (!url) throw new Error(`URL at index ${index} is empty`);
        try {
          new URL(url.startsWith('http') ? url : `https://${url}`);
        } catch {
          throw new Error(`Invalid URL at index ${index}: ${url}`);
        }
      });
    } else {
      // For single URL requests
      if (!request.url && (!request.urls || request.urls.length === 0)) {
        throw new Error("URL is required");
      }
      if (request.url) {
        try {
          new URL(request.url.startsWith('http') ? request.url : `https://${request.url}`);
        } catch {
          throw new Error(`Invalid URL: ${request.url}`);
        }
      }
    }
    
    if (!request.viewports || request.viewports.length === 0) throw new Error("At least one viewport is required");
    if (!request.themes || request.themes.length === 0) throw new Error("At least one theme is required");
    if (!params.figmaUID) throw new Error("Figma user ID is required");
  }

  private validateCodeRequest(request: CaptureFromCodeRequest, params: CaptureParams): void {
    if (!request.html) throw new Error("HTML content is required");
    if (!request.viewports || request.viewports.length === 0) throw new Error("At least one viewport is required");
    if (!request.themes || request.themes.length === 0) throw new Error("At least one theme is required");
    if (!params.figmaUID) throw new Error("Figma user ID is required");
  }

  private validateZipRequest(zipFile: File, request: CaptureFromZipRequest, params: CaptureParams): void {
    if (!zipFile) throw new Error("ZIP file is required");
    if (!request.viewports || request.viewports.length === 0) throw new Error("At least one viewport is required");
    if (!request.themes || request.themes.length === 0) throw new Error("At least one theme is required");
    if (!params.figmaUID) throw new Error("Figma user ID is required");

    const extension = zipFile.name.slice(((zipFile.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
    if (!["zip", "html", "htm"].includes(extension)) {
      throw new Error("File must be a ZIP archive (.zip) or HTML file (.html, .htm)");
    }

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (zipFile.size > MAX_FILE_SIZE) {
      throw new Error(`File is too large (${formatBytes(zipFile.size)}). Maximum size is ${formatBytes(MAX_FILE_SIZE)}.`);
    }
  }

  private prepareZipFormData(zipFile: File, request: CaptureFromZipRequest): FormData {
    const extension = zipFile.name.slice(((zipFile.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
    const formData = new FormData();

    // Ensure correct MIME type
    const correctedFile = extension === "zip"
      ? new File([zipFile], zipFile.name, { type: "application/zip", lastModified: Date.now() })
      : new File([zipFile], zipFile.name, { type: "text/html", lastModified: Date.now() });

    formData.append("file", correctedFile);

    // Add viewports and themes
    request.viewports.forEach(viewport => formData.append("viewports", viewport));
    request.themes.forEach(theme => formData.append("themes", theme));


    if (request.customViewports) {
      formData.append("customViewports", JSON.stringify(request.customViewports));
    }

    return formData;
  }

  /**
   * What the server sends with a refusal that the UI must ACT on, not merely display.
   *
   * `WebsiteCapture`, `HtmlCssCapture` and `ZipImport` all branch on `rateLimited` to open the
   * upgrade modal. A refusal that arrives as a bare Error reaches none of them.
   */
  private static readonly REFUSAL_FIELDS = [
    'rateLimited',
    'quotaExhausted',
    /** Set when the refusal is "this account is not paid", rather than "this window is full". */
    'upgradeRequired',
    'blocked',
    /**
     * Carried like the others so the server's own wording survives to the UI. Deliberately NOT
     * one of the fields that opens the upgrade modal: a challenge is not something a PRO
     * subscription removes, and offering to sell a plan in answer to "prove you are human" would
     * be both useless and insulting.
     */
    'verificationRequired',
    'used',
    'limit',
    'remainingTime',
    'nextAvailableTime',
    'nextAvailableTimeFormatted',
  ] as const;

  /**
   * An error that carries the response body it came from.
   *
   * `ApiService` already throws `HttpError` with the parsed body on `.data`, so this gives a
   * non-2xx refusal and a 200-with-`isSuccess: false` the same shape, and `failed` below only has
   * to know about one of them.
   */
  private static bodyError(body: { message?: string } | undefined, fallback: string): Error {
    const err = new Error(body?.message || fallback) as Error & { data: unknown };
    err.data = body;
    return err;
  }

  /**
   * Rethrow a failed capture WITH the server's own refusal still attached.
   *
   * This used to be `new Error(this.enhanceErrorMessage(error.message, op))`, which threw away
   * everything that made the refusal actionable. Two things went with it.
   *
   * The MESSAGE: `HttpError`'s own message is `HTTP 429`, because the readable sentence lives in
   * the body. `enhanceErrorMessage` matches none of its patterns and returns it unchanged, so a
   * user who had spent their monthly captures was shown the string "HTTP 429" — the server's
   * "You have used all 10 free captures this month..." never reached the screen.
   *
   * The FIELDS: every capture component decides between the upgrade modal and a generic error
   * banner by testing `"rateLimited" in error`. On a bare Error that is false, so a quota refusal
   * rendered as an error banner and the paywall never appeared — the one moment the modal exists
   * for.
   */
  private failed(error: unknown, operation: string): never {
    const thrown = (error ?? {}) as { message?: string; data?: unknown };
    const body = (thrown.data && typeof thrown.data === 'object' ? thrown.data : null) as
      | Record<string, unknown>
      | null;
    const serverMessage = typeof body?.message === 'string' ? body.message : undefined;
    const next = new Error(
      this.enhanceErrorMessage(serverMessage ?? thrown.message ?? 'An unknown error occurred', operation),
    ) as Error & Record<string, unknown>;
    // Prefer the body, but fall back to the error itself so a refusal thrown as a plain object
    // still carries through.
    const source = body ?? (error as Record<string, unknown> | null);
    if (source && typeof source === 'object') {
      for (const key of CaptureService.REFUSAL_FIELDS) {
        if (key in source) next[key] = source[key];
      }
    }
    throw next;
  }

  private enhanceErrorMessage(message: string, operation: string): string {
    if (message.includes("CORS") || message.includes("cross-origin")) {
      // Not "enable the CORS proxy in Advanced Options": no such option exists.
      return "Couldn't reach the capture service. Check your connection and try again — if it persists, the service may be temporarily unavailable.";
    } else if (message.includes("timeout") || message.includes("time out")) {
      return `The operation timed out. The ${operation} may be too complex to process.`;
    } else if (message.includes("network") || message.includes("connection")) {
      return "Network connection issue. Please check your internet connection and try again.";
    } else if (message.includes("parse") || message.includes("JSON")) {
      return "Error processing design data. The API response may be invalid.";
    }
    return message;
  }
}

// Export singleton instance
export const captureService = new CaptureService();